/**
 * Activities Server Functions
 *
 * 通过 TanStack Start 的 server functions 封装活动列表相关的后端调用。
 * 该模块提供按 Campaign 列出活动的 BFF 接口，自动透传 Cookie。
 */

import { createServerFn } from "@tanstack/react-start";
import type {
  ListActivitiesByCampaignApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdGetParams,
  ListActivitiesByCompanyApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdCompaniesCompanyIdGetParams,
  ListActivitiesByDealApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdCompaniesCompanyIdDealsDealIdGetParams,
} from "@/lib/api/generated";
import {
  listActivitiesByCampaignApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdGet,
  listActivitiesByCompanyApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdCompaniesCompanyIdGet,
  listActivitiesByDealApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdCompaniesCompanyIdDealsDealIdGet,
} from "@/lib/api/generated/activities/activities";
import { logInfo, logError } from "@/lib/structured-log";

/**
 * 列出指定 Campaign 的活动（分页）
 */
export const listCampaignActivitiesServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      params?: ListActivitiesByCampaignApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdGetParams;
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("activities.campaign.list.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      page: data.params?.page,
      page_size: data.params?.page_size,
    });

    try {
      const response =
        await listActivitiesByCampaignApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdGet(
          data.workspaceId,
          data.campaignId,
          data.params,
        );

      const status = response.status as number;
      if (status !== 200) {
        logError("activities.campaign.list.failed", {
          status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(JSON.stringify({ detail: "Failed to list campaign activities", status }));
      }

      // TanStack Start's type system expects { [key: string]: {} } but orval generates { [key: string]: unknown }
      // for Python's dict[str, Any]. The runtime data is correct, so we use type assertion here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return response.data as any;
    } catch (error) {
      logError("activities.campaign.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

/**
 * 列出指定 Company 的活动（分页）
 */
export const listCompanyActivitiesServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      companyId: string;
      params?: ListActivitiesByCompanyApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdCompaniesCompanyIdGetParams;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { workspaceId, campaignId, companyId, params } = data;
    logInfo("activities.company.list.server_fn_called", {
      workspace_id: workspaceId,
      campaign_id: campaignId,
      company_id: companyId,
      page: params?.page,
      page_size: params?.page_size,
    });

    try {
      const response =
        await listActivitiesByCompanyApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdCompaniesCompanyIdGet(
          workspaceId,
          campaignId,
          companyId,
          params,
        );

      const status = response.status as number;
      if (status !== 200) {
        logError("activities.company.list.failed", {
          status,
          workspace_id: workspaceId,
          campaign_id: campaignId,
          company_id: companyId,
        });
        throw new Error(JSON.stringify({ detail: "Failed to list company activities", status }));
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return response.data as any;
    } catch (error) {
      logError("activities.company.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: workspaceId,
        campaign_id: campaignId,
        company_id: companyId,
      });
      throw error;
    }
  });

/**
 * 列出指定 Deal 的活动（分页）
 */
export const listDealActivitiesServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      companyId: string;
      dealId: string;
      params?: ListActivitiesByDealApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdCompaniesCompanyIdDealsDealIdGetParams;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { workspaceId, campaignId, companyId, dealId, params } = data;
    logInfo("activities.deal.list.server_fn_called", {
      workspace_id: workspaceId,
      campaign_id: campaignId,
      company_id: companyId,
      deal_id: dealId,
      page: params?.page,
      page_size: params?.page_size,
    });

    try {
      const response =
        await listActivitiesByDealApiWorkspacesWorkspaceIdActivitiesCampaignsCampaignIdCompaniesCompanyIdDealsDealIdGet(
          workspaceId,
          campaignId,
          companyId,
          dealId,
          params,
        );

      const status = response.status as number;
      if (status !== 200) {
        logError("activities.deal.list.failed", {
          status,
          workspace_id: workspaceId,
          campaign_id: campaignId,
          company_id: companyId,
          deal_id: dealId,
        });
        throw new Error(JSON.stringify({ detail: "Failed to list deal activities", status }));
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return response.data as any;
    } catch (error) {
      logError("activities.deal.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: workspaceId,
        campaign_id: campaignId,
        company_id: companyId,
        deal_id: dealId,
      });
      throw error;
    }
  });
